import { Component, EventEmitter, inject, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  LucideAngularModule,
  Trash2Icon,
  PlusIcon,
  MinusIcon,
  ShoppingCartIcon,
  EditIcon,
  CheckIcon,
  CircleCheckIcon,
  XIcon,
  ExternalLinkIcon,
} from 'lucide-angular';
import { CartService } from '../../services/cart.service';
import { smartTitle } from '../../utils/pipes/smart-capitalize';
import { CartItem } from '../../types/Cart.types';
import { PriceDisplayComponent } from '../shared/price-display/price-display.component';

@Component({
  selector: 'allas-cart',

  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    PriceDisplayComponent,
  ],
  template: `
    <div class="fs-condensed flex flex-col gap-1">
      <!-- Botón cerrar carrito -->
      <div class="flex items-center justify-between p-2">
        <div class="flex items-center gap-2">
          <lucide-icon
            [name]="shoppingCartIcon"
            class="size-6 text-gray-600"
          ></lucide-icon>
          Mi Carrito
        </div>
        <button
          (click)="handleClickOnCloseCart()"
          class="p-1 text-gray-800 hover:text-gray-900 transition-colors"
          title="Cerrar carrito"
        >
          <lucide-icon [name]="cancelIcon" class="w-5 h-5"></lucide-icon>
        </button>
      </div>

      <!-- Header compacto -->
      @if (cartService.totalItems() > 0) {
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-2">
          <!-- Icono de check azul con circulo al rededor -->
          <lucide-icon
            [name]="circleCheckIcon"
            class="w-5 h-5 text-appPrimary-500"
          ></lucide-icon>
          <span class="font-medium text-gray-900 text-sm">
            Agregado a Carrito
          </span>
        </div>
      </div>
      }

      <!-- Contenido del carrito compacto -->
      <div class="max-h-64 overflow-y-auto">
        @if (cartService.isEmpty()) {
        <!-- Carrito vacío compacto -->
        <div class="p-2 text-center">
          <div class="text-gray-400 mb-2">
            <lucide-icon
              [name]="shoppingCartIcon"
              class="size-6 mx-auto"
            ></lucide-icon>
          </div>
          <p class="text-gray-500 text-xs mb-3">Sin productos</p>
        </div>
        } @else {

        <!-- Linea divisoria -->
        <hr class="my-2 border-gray-200" />

        <!-- Items del carrito ultra compactos -->
        <div class="p-2 space-y-2">
          @for (item of cartService.cartItems(); track item.id) {
          <article class="rounded-md p-2 border border-gray-400 relative">
            <!-- Botón eliminar en la esquina superior derecha -->
            <button
              (click)="removeItem(item)"
              class="absolute top-1 right-1 p-1 text-gray-400 hover:text-red-600 transition-colors"
              title="Eliminar producto"
            >
              <lucide-icon [name]="trashIcon" class="w-3 h-3"></lucide-icon>
            </button>

            <!-- Mini card del producto -->
            <div class="flex items-start space-x-3">
              <!-- Imagen del producto -->
              <div class="flex-shrink-0">
                @if (item.producto?.imagenes?.thumb &&
                item.producto!.imagenes!.thumb.length > 0) {
                <img
                  [src]="item.producto!.imagenes!.thumb[0]"
                  [alt]="item.producto!.nombre_web"
                  class="h-12 w-auto object-cover rounded-md"
                />
                } @else {
                <div
                  class="w-12 h-12 bg-gray-200 rounded-md flex items-center justify-center"
                >
                  <lucide-icon
                    [name]="shoppingCartIcon"
                    class="w-6 h-6 text-gray-400"
                  ></lucide-icon>
                </div>
                }
              </div>

              <!-- Información del producto -->
              <div class="flex-1 min-w-0 pr-4">
                <!-- Nombre del producto -->
                <h4
                  class="font-medium text-gray-900 text-xs truncate leading-tight mb-1"
                >
                  {{
                    item.producto?.nombre_web ||
                      smartTitle(item.grupo?.name || '')
                  }}
                </h4>

                <!-- SKU y Part Number -->
                <div class="text-[10px] font-light text-gray-500 mb-2">
                  @if (item.producto?.part_number && item.producto?.sku) {
                  <div>
                    Parte #{{ item.producto!.part_number }} Código #{{
                      item.producto!.sku
                    }}
                  </div>
                  } @else if (item.producto?.part_number) {
                  <div>Parte #{{ item.producto!.part_number }}</div>
                  } @else if (item.producto?.sku) {
                  <div>Código #{{ item.producto!.sku }}</div>
                  }
                </div>

                <!-- Controles de cantidad y precio en una fila -->
                <div class="flex items-center justify-between gap-3 flex-wrap">
                  <!-- Controles de cantidad -->
                  <div class="flex items-center gap-1">
                    <button
                      (click)="decreaseQuantity(item)"
                      class="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                      title="Disminuir cantidad"
                    >
                      <lucide-icon
                        [name]="minusIcon"
                        class="w-3 h-3 text-gray-700"
                      ></lucide-icon>
                    </button>
                    <span
                      class="min-w-[24px] text-center text-xs font-semibold text-gray-900"
                    >
                      {{ item.cantidad }}
                    </span>
                    <button
                      (click)="increaseQuantity(item)"
                      class="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                      title="Aumentar cantidad"
                    >
                      <lucide-icon
                        [name]="plusIcon"
                        class="w-3 h-3 text-gray-700"
                      ></lucide-icon>
                    </button>
                  </div>

                  <!-- Precio -->
                  @if (item.producto?.precio) {
                  <div class="flex-shrink-0">
                    <allas-price-display
                      [price]="item.producto!.precio"
                    ></allas-price-display>
                  </div>
                  }
                </div>
              </div>
            </div>
          </article>
          }
        </div>
        }
      </div>

      <!-- Footer compacto -->
      @if (!cartService.isEmpty()) {
      <!-- Linea divisoria -->
      <hr class="my-2 border-gray-200" />

      <!-- Contenido del carrito compacto -->
      <div class="flex flex-col gap-2">
        <!-- Resumen de la compra -->
        <div class="text-sm text-gray-800 justify-center text-center font-bold">
          Sub total Carrito: L.{{ getSubtotal() | number : '1.2-2' }}
        </div>
        <!-- Botón principal compacto -->
        <a
          href="/carrito"
          class="w-full grid place-content-center bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 rounded-md transition-colors font-normal"
        >
          VER CARRITO
        </a>

        <!-- VAMOS A COMENTAR ESTO PORQUE SE VE HORRIBLE -->
        <!-- <h2 class="justify-center text-center font-normal my-2">
          LISTA A COTIZAR
        </h2> -->
        <!-- Detalle de la lista a cotizar -->
        <!-- <div class="bg-white p-3">
          <div class="flex justify-end mb-2">
            <span class="text-xs font-thin text-gray-800">CANTIDAD:</span>
          </div>

          <div class="flex justify-between items-center mb-2 pb-1">
            <div class="text-md font-normal text-gray-900">TOTAL PRODUCTOS</div>
            <div class="text-lg font-bold text-gray-900">
              {{ cartService.cartItems().length }}
            </div>
          </div>

          <div>
            @for (item of cartService.cartItems(); track item.id) {
            <div class="flex justify-between items-center py-1">
              <div
                class="flex-1 text-md font-medium text-gray-900 truncate pr-2"
              >
                {{
                  smartTitle(
                    item.producto?.nombre_web || item.grupo?.name || ''
                  )
                }}
              </div>
              <div class="text-sm font-semibold text-gray-900">
                {{ item.cantidad }}
              </div>
            </div>
            }
          </div>
        </div> -->

        <!-- Botón principal compacto -->
        <!-- <div class="mb-5 mx-6">
          <button
            (click)="proceedToCheckout()"
            class="w-full bg-appPrimary-500 hover:bg-blue-700 text-white py-3 rounded-lg transition-colors font-normal text-lg fs-condensed"
          >
            COTIZAR
          </button>
        </div> -->

        <!-- Acciones secundarias compactas -->
        <a
          href="/repuestos"
          class="w-full text-center text-xs py-1 font-medium text-gray-700 transition-colors underline"
        >
          Continuar Comprando
        </a>
      </div>
      }
    </div>
  `,
})
export class CartComponent {
  cartService = inject(CartService);
  private router = inject(Router);

  // Icons
  readonly shoppingCartIcon = ShoppingCartIcon;
  readonly trashIcon = Trash2Icon;
  readonly plusIcon = PlusIcon;
  readonly minusIcon = MinusIcon;
  readonly editIcon = EditIcon;
  readonly checkIcon = CheckIcon;
  readonly circleCheckIcon = CircleCheckIcon;
  readonly cancelIcon = XIcon;
  readonly externalIcon = ExternalLinkIcon;

  // Estado para edición de notas
  editingNotes = signal<number | null>(null);
  tempNotes = signal<string>('');

  smartTitle = smartTitle;

  // Outputs
  @Output() closeCart = new EventEmitter<void>();

  handleClickOnCloseCart(): void {
    this.closeCart.emit();
  }

  getSubtotal(): number {
    return this.cartService.cartItems().reduce((total, item) => {
      return total + item.producto!.precio * item.cantidad;
    }, 0);
  }

  // Métodos para manejar la cantidad de productos
  increaseQuantity(item: CartItem): void {
    const currentQuantity = item.cantidad + 1;
    this.cartService.updateItemInCart({
      ...item,
      cantidad: currentQuantity,
    });
  }

  decreaseQuantity(item: CartItem): void {
    const currentQuantity = item.cantidad - 1;

    if (currentQuantity >= 1) {
      this.cartService.updateItemInCart({
        ...item,
        cantidad: currentQuantity,
      });
    }
  }

  removeItem(item: CartItem): void {
    this.cartService.removeItemFromCart(item.id);
  }
}
